import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Trash2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { ContainerType } from 'src/trpc';

export interface PeriodPricingTier {
  basePriceInCents: number;
  includedTonnage: number;
  includedDays: number;
  additionalDayRateInCents: number;
  additionalTonRateInCents: number;
}

interface ContainerPeriodMatrixProps {
  containerPeriods: Record<string, PeriodPricingTier>;
  onContainerPeriodsChange: (data: Record<string, PeriodPricingTier>) => void;
  containers: ContainerType[];
}

export const ContainerPeriodMatrix: FC<ContainerPeriodMatrixProps> = ({
  containerPeriods,
  onContainerPeriodsChange,
  containers,
}) => {
  const [selectedContainers, setSelectedContainers] = useState(Object.keys(containerPeriods));

  const addContainer = (containerId: string) => {
    if (!selectedContainers.includes(containerId)) {
      setSelectedContainers([...selectedContainers, containerId]);
      const newData = { ...containerPeriods };
      if (!newData[containerId]) {
        newData[containerId] = {
          basePriceInCents: 0,
          includedTonnage: 0,
          includedDays: 0,
          additionalDayRateInCents: 0,
          additionalTonRateInCents: 0,
        };
      }
      onContainerPeriodsChange(newData);
    }
  };

  const removeContainer = (containerId: string) => {
    setSelectedContainers(selectedContainers.filter((id) => id !== containerId));
    const newData = { ...containerPeriods };
    delete newData[containerId];
    onContainerPeriodsChange(newData);
  };

  const updateContainerPeriod = (
    containerId: string,
    field: keyof PeriodPricingTier,
    value: number,
  ) => {
    const newData = { ...containerPeriods };
    if (newData[containerId]) {
      newData[containerId] = {
        ...newData[containerId],
        [field]: value,
      };
      onContainerPeriodsChange(newData);
    }
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
        <div className="font-medium mb-1">Single Period Pricing Rule</div>
        <div>
          This rule configures pricing for one specific period (e.g., 10-day, 20-day, or 30-day).
          Create separate rules for each period you want to offer.
        </div>
      </div>

      <div>
        <Label>Select Containers</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start bg-white hover:bg-gray-50 text-muted"
            >
              <Search className="mr-2 h-4 w-4 text-muted" />
              Add containers for this period pricing
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search containers..." />
              <CommandList>
                <CommandEmpty>No containers found.</CommandEmpty>
                <CommandGroup>
                  {containers
                    .filter((container) => !selectedContainers.includes(container._id!))
                    .map((container) => (
                      <CommandItem
                        key={container._id!}
                        value={container.name}
                        onSelect={() => addContainer(container._id!)}
                        className="flex items-center hover:bg-gray-100 cursor-pointer"
                      >
                        <Check className="mr-2 h-4 w-4 text-primary opacity-0" />
                        <span className="flex-1">{container.name}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selectedContainers.length > 0 && (
        <div className="space-y-4">
          {selectedContainers.map((containerId) => {
            const container = containers.find((c) => c._id === containerId);
            const period = containerPeriods[containerId];

            // Skip rendering if period data is not available
            if (!period) {
              return null;
            }

            return (
              <Card key={containerId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {container?.name || 'Unknown Container'}
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeContainer(containerId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Base Price</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={period.basePriceInCents / 100}
                        onChange={(e) =>
                          updateContainerPeriod(
                            containerId,
                            'basePriceInCents',
                            Math.round(parseFloat(e.target.value) * 100),
                          )
                        }
                        placeholder="295.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Included Days</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={period.includedDays}
                        onChange={(e) =>
                          updateContainerPeriod(
                            containerId,
                            'includedDays',
                            parseInt(e.target.value) || 1,
                          )
                        }
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Included Tonnage</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={period.includedTonnage}
                        onChange={(e) =>
                          updateContainerPeriod(
                            containerId,
                            'includedTonnage',
                            parseFloat(e.target.value),
                          )
                        }
                        placeholder="2.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Additional Day Rate</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={period.additionalDayRateInCents / 100}
                        onChange={(e) =>
                          updateContainerPeriod(
                            containerId,
                            'additionalDayRateInCents',
                            Math.round(parseFloat(e.target.value) * 100),
                          )
                        }
                        placeholder="15.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Additional Ton Rate</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={period.additionalTonRateInCents / 100}
                        onChange={(e) =>
                          updateContainerPeriod(
                            containerId,
                            'additionalTonRateInCents',
                            Math.round(parseFloat(e.target.value) * 100),
                          )
                        }
                        placeholder="75.00"
                      />
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    Example: {formatCurrency(period.basePriceInCents)} for {period.includedDays}{' '}
                    days ({period.includedTonnage} tons included) +{' '}
                    {formatCurrency(period.additionalDayRateInCents)}/extra day +{' '}
                    {formatCurrency(period.additionalTonRateInCents)}/extra ton
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
